/**
 * Access-request workflow — shared enums/consts for api (built pkg) and web
 * (source alias). Replaces ad-hoc Slack DMs for resource access: requester says
 * what they need and for how long, layer-1 reviewers get assigned+notified,
 * reviewers manually escalate to the next layer when they can't resolve, and
 * approve = the grant already happened out-of-band.
 *
 * Everything here is deliberately shared: the picker, the risk badge, the review
 * path shown to the requester, the Slack copy and the API's stored snapshot all
 * read the SAME labels and the SAME riskTier(), so the UI can't promise a review
 * path the server doesn't record.
 */

/**
 * What can be requested. Widened from `database | other` to the twelve systems
 * people actually ask for, because "Other" was absorbing most requests and a
 * reviewer had to read the prose to learn what was even being requested.
 *
 * The DB CHECK is a superset that still permits legacy values, so historical rows
 * validate.
 */
export type AccessResourceType =
  | 'database'
  | 'secret'
  | 'cluster'
  | 'droplet'
  | 'repo'
  | 'aws'
  | 'cloudflare'
  | 'grafana'
  | 'oauth'
  | 'payments'
  | 'vpn'
  | 'other';

export const ACCESS_RESOURCE_TYPES: AccessResourceType[] = [
  'database',
  'secret',
  'cluster',
  'droplet',
  'repo',
  'aws',
  'cloudflare',
  'grafana',
  'oauth',
  'payments',
  'vpn',
  'other',
];

/** Formal display labels for the resource-type picker. */
export const ACCESS_RESOURCE_TYPE_LABELS: Record<AccessResourceType, string> = {
  database: 'Database',
  secret: 'Secret / credentials',
  cluster: 'Kubernetes cluster',
  droplet: 'Server / droplet',
  repo: 'Code repository',
  aws: 'AWS account',
  cloudflare: 'Cloudflare',
  grafana: 'Grafana / observability',
  oauth: 'OAuth / SSO app',
  payments: 'Payments',
  vpn: 'VPN / SecureLink',
  other: 'Other',
};

/**
 * The concrete provider behind each system, shown as a suffix in the option label
 * ("Database — MongoDB Atlas / DO Postgres"). Removes a class of wrong picks:
 * people choosing "Database" when they meant the Postgres audit store, or
 * "Secret" when they meant an AWS secret.
 */
export const ACCESS_RESOURCE_TYPE_PROVIDERS: Record<AccessResourceType, string> = {
  database: 'MongoDB Atlas / DO Postgres',
  secret: 'GCP Secret Manager',
  cluster: 'DigitalOcean Kubernetes',
  droplet: 'DigitalOcean',
  repo: 'GitLab',
  aws: 'AWS',
  cloudflare: 'Cloudflare',
  grafana: 'Grafana Cloud',
  oauth: 'Google Workspace',
  payments: 'Stripe',
  vpn: 'WireGuard / SecureLink',
  other: '',
};

/** The five optgroups the picker renders, in order. */
export const ACCESS_RESOURCE_TYPE_GROUPS: { label: string; types: AccessResourceType[] }[] = [
  { label: 'Data & secrets', types: ['database', 'secret'] },
  { label: 'Infrastructure', types: ['cluster', 'droplet'] },
  { label: 'Code & CI', types: ['repo'] },
  { label: 'Cloud & network', types: ['aws', 'cloudflare', 'vpn'] },
  { label: 'Apps & tooling', types: ['grafana', 'oauth', 'payments', 'other'] },
];

export type AccessDurationKind = 'permanent' | 'temporary';

/**
 * Selectable durations, in days. A closed list rather than a free number so the
 * expiry sweep has predictable buckets and reviewers see familiar windows.
 */
export const ACCESS_DURATION_DAYS = [1, 7, 30, 90, 180, 365, 730] as const;

export type AccessRiskTier = 'standard' | 'elevated' | 'high';

/**
 * Systems where a mistake is expensive or hard to undo: production data, money,
 * long-lived credentials, and anything that grants further access.
 */
export const SENSITIVE_RESOURCE_TYPES: AccessResourceType[] = [
  'secret',
  'payments',
  'database',
  'aws',
  'droplet',
  'cloudflare',
];

/**
 * Risk tier, computed at submit and SNAPSHOT on the row — so tightening this
 * function later never rewrites the tier that history was reviewed under.
 *
 * Sensitive AND permanent is high: the two multiply, because an open-ended grant
 * to a sensitive system is the thing nobody remembers to revoke. Either one alone
 * is elevated.
 */
export function riskTier(type: AccessResourceType, kind: AccessDurationKind): AccessRiskTier {
  const sensitive = SENSITIVE_RESOURCE_TYPES.includes(type);
  const permanent = kind === 'permanent';
  if (sensitive && permanent) return 'high';
  if (sensitive || permanent) return 'elevated';
  return 'standard';
}

/** What each tier means for the requester, shown next to the badge on the form. */
export const ACCESS_RISK_TIER_BLURB: Record<AccessRiskTier, string> = {
  standard: 'A layer-1 reviewer can approve this.',
  elevated: 'A layer-1 reviewer can approve, and may escalate before deciding.',
  high: 'Expect escalation — an open-ended grant to a sensitive system gets a second look.',
};

export type AccessRequestStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export const ACCESS_REQUEST_STATUSES: AccessRequestStatus[] = [
  'pending',
  'approved',
  'denied',
  'cancelled',
];

/**
 * Max length for the access-request list title (`resourceName`).
 * DB column is unbounded `text`; this is a product limit so list rows stay
 * readable (GCP project ids, Grafana hosts, DB names all fit well under it).
 */
export const ACCESS_REQUEST_RESOURCE_NAME_MAX = 120;

/** NULL in the DB = sign-off never requested. */
export type MaintainerSignoff = 'requested' | 'approved' | 'declined';

export type AccessRequestEventKind =
  | 'created'
  | 'assigned'
  | 'escalated'
  | 'maintainer_signoff_requested'
  | 'maintainer_signoff'
  | 'commented'
  | 'decided'
  | 'cancelled';
